import { ModuleKey, ModuleDefinition } from '../types/subscriptionTypes';

// ─── Core Program ───

export const CORE_PROGRAM_DEFAULT_RATE = 99.00;

export const CORE_FEATURES = [
    'Inspections & Daily Service',
    'Equipment Management',
    'Daily Briefing',
    'Reports',
    'User Management',
    'Resources Library',
    'Form Templates',
    'Email Support',
];

// ─── Trial Config ───

export const TRIAL_DURATION_DAYS = 30;
export const TRIAL_EMAIL_REMINDERS = [7, 3, 0]; // days before expiration
export const ADD_ON_GRACE_PERIOD_DAYS = 5; // TBD — placeholder pending client decision

// ─── Payment Failure Config ───

export const GRACE_PERIOD_DAYS = 14;
export const PAYMENT_RETRY_DAYS = [3, 7, 12]; // days after initial failure
export const MAX_RETRY_ATTEMPTS = 3;

// ─── Data Retention ───

export const DATA_RETENTION_DAYS = 90; // days after cancelEffectiveDate before hard delete

// ─── Module Definitions ───

export const MODULE_CONFIG: Record<ModuleKey, ModuleDefinition> = {
    pm: {
        key: 'pm',
        name: 'Preventive Maintenance',
        shortName: 'PM',
        description: 'Scheduled service intervals, parts tracking, and automated reminders for every piece of equipment.',
        features: [
            'PM scheduling & service queue',
            'Custom service levels configuration',
            'Parts & vendor management',
            'Due-soon & overdue alerts',
        ],
        pricingModel: 'per-asset',
        monthlyRate: 2.00,
        rateLabel: '$2 /form/mo',
        countNoun: 'forms',
        icon: 'pm',
        enrollmentField: 'pmModuleEnrollment',
    },
    tires: {
        key: 'tires',
        name: 'Tire Management',
        shortName: 'Tires',
        description: 'Track tire inventory, axle assignments, tread depth, and replacement thresholds across your fleet.',
        features: [
            'Tire inventory & assignment',
            'Axle & position configuration',
            'Tread depth & lifespan tracking',
            'Replacement threshold alerts',
        ],
        pricingModel: 'per-asset',
        monthlyRate: 1.50,
        rateLabel: '$1.50 /asset/mo',
        countNoun: 'assets',
        icon: 'tires',
        enrollmentField: 'tiresModuleEnrollment',
    },
    training: {
        key: 'training',
        name: 'Safety & Training',
        shortName: 'Training',
        description: 'Full training plans, MSHA/OSHA compliance, scheduled topic delivery.',
        features: [
            'Training plans',
            '365-day schedules',
            'User compliance',
            'Affidavits',
        ],
        pricingModel: 'flat',
        monthlyRate: 45.00,
        rateLabel: '$45 /mo',
        icon: 'training',
        enrollmentField: 'trainingModuleEnrollment',
    },
    costTracking: {
        key: 'costTracking',
        name: 'Cost / Expense Tracking',
        shortName: 'Cost Tracking',
        description: 'Expense logging, vendor invoicing, labor rates, $/UM reports.',
        features: [
            'Expense logging',
            'Vendor invoices',
            'Labor rates',
            '$/UM reports',
        ],
        pricingModel: 'flat',
        monthlyRate: 59.00,
        rateLabel: '$59 /mo',
        icon: 'costTracking',
        enrollmentField: 'costTrackingModuleEnrollment',
    },
    skillTracking: {
        key: 'skillTracking',
        name: 'User Skillsets',
        shortName: 'Skillsets',
        description: 'Track quals, certifications, expiration dates, authorizations.',
        features: [
            'Skill matrix',
            'Cert alerts',
            'Equipment auth',
            'Training records',
        ],
        pricingModel: 'flat',
        monthlyRate: 29.00,
        rateLabel: '$29 /mo',
        icon: 'skillTracking',
        enrollmentField: 'skillTrackingModuleEnrollment',
    },
};

// ─── All module keys in display order ───

export const MODULE_KEYS_ORDERED: ModuleKey[] = ['pm', 'tires', 'training', 'costTracking', 'skillTracking'];

// ─── Cancel reason options ───

export const CANCEL_REASONS = [
    { value: 'too-expensive', label: 'Too expensive', description: "The cost isn't justifying the value for us." },
    { value: 'missing-features', label: 'Missing features', description: "TotalEquipIQ doesn't do what we need." },
    { value: 'project-ended', label: 'Project ended / seasonal', description: "We don't need it right now but might come back." },
    { value: 'switching', label: 'Switching to another tool', description: 'Found a different solution.' },
    { value: 'other', label: 'Other', description: 'Tell us in the next step.' },
] as const;
